import { Box, BoxProps, HStack, Spinner, Text, useColorModeValue } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useClient } from '../../core/client/ClientProvider'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { AnswerStep } from '../../types/chatTypes'

interface AnswerLoaderProps extends BoxProps {
  step?: AnswerStep
}

const STEP_ROTATION_MS = 3000
const THINKING_ROTATION_MS = 6000
// Processing attached files can take minutes (OCR, one description per image).
const SLOW_PROCESSING_MS = 15000

function resolveThinkingMessages(
  thinkingMessages: Record<string, string[]> | undefined,
  language: string,
  fallback: string,
): string[] {
  const messages = thinkingMessages?.[language] ?? thinkingMessages?.['en']
  return messages && messages.length > 0 ? messages : [fallback]
}

/** The status line for a step: one line per query or file, so several rotate. */
function stepLines(step: AnswerStep | undefined, t: (key: string, options?: object) => string): string[] {
  const items = step?.items ?? []
  switch (step?.kind) {
    case 'checking':
      return [t('answerStatus.checking')]
    case 'internet':
      return items.map(query => t('answerStatus.internet', { query }))
    case 'knowledge':
      return items.map(query => t('answerStatus.knowledge', { query }))
    case 'document':
      return [t('answerStatus.document')]
    case 'files':
      return [t('answerStatus.files', { count: items.length, name: items[0] })]
    case 'image':
      return items.length ? items.map(name => t('answerStatus.imageNamed', { name })) : [t('answerStatus.image')]
    case 'graph':
      return [t('tools.graph_search.active')]
    default:
      return []
  }
}

export const AnswerLoader: React.FC<AnswerLoaderProps> = ({ step, ...props }) => {
  const { config } = useClient()
  const { t, currentLanguage } = useLanguage()
  const thinking = resolveThinkingMessages(
    config?.theme.customTexts.thinkingMessages,
    currentLanguage,
    t('chat.thinking'),
  )
  // Start at a random thinking message per answer; the rotation below still visits every message in turn.
  const [messageIndex, setMessageIndex] = useState(() => Math.floor(Math.random() * thinking.length))
  const [isSlow, setIsSlow] = useState(false)

  // After a while say that a long processing is normal, so it does not look like a hang.
  const isProcessing = step?.kind === 'files' || step?.kind === 'image'
  useEffect(() => {
    setIsSlow(false)
    if (!isProcessing) return
    const timer = setTimeout(() => setIsSlow(true), SLOW_PROCESSING_MS)
    return () => clearTimeout(timer)
  }, [isProcessing])

  const lines = stepLines(step, t)
  const messages = lines.length ? [...lines, ...(isSlow ? [t('answerStatus.slowFiles')] : [])] : thinking

  useEffect(() => {
    if (messages.length <= 1) return
    const interval = setInterval(
      () => setMessageIndex(previous => previous + 1),
      lines.length ? STEP_ROTATION_MS : THINKING_ROTATION_MS,
    )
    return () => clearInterval(interval)
  }, [messages.length, lines.length])

  const shimmerGradient = useColorModeValue(
    'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)',
    'linear-gradient(90deg, rgba(26,32,44,0) 0%, rgba(26,32,44,0.2) 50%, rgba(26,32,44,0) 100%)',
  )

  return (
    <Box
      {...props}
      position='relative'
      _after={{
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: shimmerGradient,
        animation: 'shimmer 2s infinite linear',
        zIndex: 1,
      }}
      sx={{
        '@keyframes shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      }}
    >
      <HStack spacing={1.5}>
        <Spinner boxSize='0.875rem' thickness='1.5px' color='chat.muted' speed='0.8s' flexShrink={0} />
        <Text fontSize='sm' color='chat.muted' noOfLines={1}>
          {messages[messageIndex % messages.length]}
        </Text>
      </HStack>
    </Box>
  )
}
